import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Header, Footer } from './components';
import { BibleVersionProvider } from './context/bibleVersionContext';
import { VerseModalProvider } from "./context/verseModalContext";
import AccountModal from "./components/account/AccountModal";
// Imported directly, not via the pages barrel -- the barrel re-exports every
// page, which would pull them all back into the initial chunk.
import Home from './pages/home/Home';
import PageNotFound from './pages/pageNotFound/pageNotFound.js';
import './App.css';
// Global so every page gets the same breadcrumb. Page stylesheets used to
// define these four class names eight different ways.
import './styles/breadcrumbs.css';
// Imported last so the shared modal design wins any specificity tie
// against a page stylesheet. See src/styles/modalKit.css.
import './styles/modalKit.css';

/*
 * Imported here rather than beside its component, which is where it belongs.
 *
 * PagePeople renders from PageHowTo, and pages import PageHowTo and
 * ContinueStory in whatever order they were written -- some before, some
 * after. That made PagePeople.css land in a different position in different
 * route chunks, and mini-css-extract-plugin refuses to pick an order it cannot
 * prove: "Conflicting order". Locally that is a warning; CI sets CI=true, CRA
 * turns warnings into errors, and the production build fails.
 *
 * The two stylesheets are unrelated -- .ctb-pagePeople* against
 * continueStory's own classes -- so the order genuinely does not matter. This
 * puts the file in the main bundle at one fixed position, which removes the
 * ambiguity rather than asking every page to import in the same order.
 */
import './components/pagePeople/PagePeople.css';

/* Same reason as the line above: this renders from PageHowTo, so colocating it
   would put it in route chunks in an order webpack cannot settle. */
import './components/studyPath/StudyPathStrip.css';

/* Same reason again: ten route chunks import FilterBar, each at whatever point
   in its own import list it was added, so colocating this would make its
   position in the merged stylesheet depend on which page loaded first. */
import './components/filterBar/FilterBar.css';

/* Same reason: the save button renders from PageHowTo, the person page and the
   verse modal, so it would otherwise enter many chunks in no fixed order. */
import './components/saveToStudy/SaveToStudyButton.css';

/* Same reason: the Gospels and Kings explorers both render it. */
import './components/waysIn/WaysIn.css';
import ScrollToTop from './components/scrollToTop';
import RouteMetadata from './components/routeMetadata';
import RouteErrorBoundary from './components/routeErrorBoundary';
import ModalNavScrollReset from './components/modalNavScrollReset';
import PersonReturnPill from './components/personReturnPill/PersonReturnPill';
import PageReturnPill from './components/pageReturnPill/PageReturnPill';
import VersionUpdatePill from './components/versionUpdatePill/VersionUpdatePill';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

/*
 * Route components load on demand.
 *
 * Everything used to import eagerly from the pages barrel, so the homepage
 * pulled all fourteen Leaflet map modes and the whole data set into one
 * ~1MB gzipped bundle. Home and PageNotFound stay eager: Home is the landing
 * route, and PageNotFound renders inline from the person and battle pages.
 */
const Abraham = lazy(() => import("./pages/genealogy/TerahAbraham/Abraham"));
const AdamNoah = lazy(() => import("./pages/genealogy/AdamNoah"));
const Battles = lazy(() => import("./pages/battles/battles.js"));
const BetweenTheTestaments = lazy(() => import("./pages/timelines/betweenTheTestaments/betweenTheTestaments"));
const CanonFormation = lazy(() => import("./pages/biblicalEvents/canonFormation/canonFormation.js"));
const ChurchHistory = lazy(() => import("./pages/newTestament/churchHistory/churchHistory"));
const ChurchLetters = lazy(() => import("./pages/newTestament/churchLetters/churchLetters"));
const Codex = lazy(() => import("./pages/biblicalEvents/codex/codex.js"));
const ExplorersIndex = lazy(() => import("./pages/explorers/ExplorersIndex"));
const David = lazy(() => import("./pages/genealogy/kingdomLine/David"));
const DividedKingdoms = lazy(() => import("./pages/oldTestament/divided-kingdoms/dividedKingdoms"));
const EarlyChurchHistory = lazy(() => import("./pages/timelines/historicalTimelines/earlyChurchHistory"));
const Ecclesiastes = lazy(() => import("./pages/oldTestament/wisdoms/ecclesiastes/ecclesiastes"));
const EnemiesOfTheBible = lazy(() => import("./pages/enemiesOfTheBible/eneminesOfTheBible"));
const EarlyChurchFathers = lazy(() => import("./pages/earlyChurchFathers/earlyChurchFathers"));
const Esau = lazy(() => import("./pages/genealogy/IsaacLine/Esau"));
const EventsExplorer = lazy(() => import("./pages/biblicalEvents/eventsExplorer/eventsExplorer.js"));
const Exodus = lazy(() => import("./pages/oldTestament/torah/exodus/pathThroughExodus"));
const Genesis = lazy(() => import("./pages/oldTestament/torah/Genesis/pathThroughGenesis"));
const Ham = lazy(() => import("./pages/genealogy/NoahTerah/Ham"));
const Haran = lazy(() => import("./pages/genealogy/TerahAbraham/Haran"));
const Jacob = lazy(() => import("./pages/genealogy/IsaacLine/Jacob"));
const Japheth = lazy(() => import("./pages/genealogy/NoahTerah/Japeth"));
const Job = lazy(() => import("./pages/oldTestament/wisdoms/job/job"));
const Joshua = lazy(() => import("./pages/oldTestament/histories/joshua/joshuaEvents"));
const JourneyThroughTheWilderness = lazy(() => import("./pages/oldTestament/torah/wildernessEvents/journeyThroughTheWilderness"));
const Judges = lazy(() => import("./pages/oldTestament/histories/judges/judgesEvents"));
const Kings = lazy(() => import("./pages/genealogy/kingdomLine/KingdomOfJudahLine.js"));
const LeviLine = lazy(() => import("./pages/genealogy/priestlyLineage/leviPriestly"));
const Leviticus = lazy(() => import("./pages/oldTestament/torah/leviticus/pathThroughLeviticus"));
const MajorProphets = lazy(() => import("./pages/oldTestament/propehts/majorProphets/majorProphets"));
const MinorProphets = lazy(() => import("./pages/oldTestament/propehts/minorProphets/prophets.js"));
const MosesAaron = lazy(() => import("./pages/genealogy/priestlyLineage/mosesAaron"));
const Nahor = lazy(() => import("./pages/genealogy/TerahAbraham/Nahor"));
const NationsOfTheBible = lazy(() => import("./pages/nationsOfTheBible/nationsOfTheBible"));
const MyStudy = lazy(() => import("./pages/myStudy/MyStudy"));
const Privacy = lazy(() => import("./pages/privacy/Privacy"));
const Numbers = lazy(() => import("./pages/oldTestament/torah/numbers/pathThroughNumbers"));
const PersonProfilePage = lazy(() => import("./pages/searchviews/PersonProfileView/PersonProfilePage.js"));
const PostExile = lazy(() => import("./pages/oldTestament/postExile/postExile"));
const Proverbs = lazy(() => import("./pages/oldTestament/wisdoms/proverbs/proverbs"));
const Psalms = lazy(() => import("./pages/oldTestament/wisdoms/psalms/psalms"));
const Revelation = lazy(() => import("./pages/newTestament/revelation/revelation.js"));
const Saul = lazy(() => import("./pages/genealogy/kingdomLine/Saul"));
const SearchPage = lazy(() => import("./pages/searchviews/MultipleSearchView/SearchPage.js"));
const SecondTempleLeadership = lazy(() => import("./pages/timelines/secondTempleLeadership/secondTempleLeadership"));
const Shem = lazy(() => import("./pages/genealogy/NoahTerah/Shem"));
const SongOfSongs = lazy(() => import("./pages/oldTestament/wisdoms/songOfSongs/songOfSongs"));
const TableOfHistoricalTimelineAlexanderToEarlyChurch = lazy(() => import("./pages/timelines/historicalTimelines/HistoricalTimelineAlexanderToEarlyChurch"));
const TableOfHistoricalTimelineDavidTOAlexander = lazy(() => import("./pages/timelines/historicalTimelines/HistoricalTimelineDavidToAlexander"));
const WalkThroughTheGospels = lazy(() => import("./pages/newTestament/gospels/aWalkThroughTheGospels.js"));
const WhoRuledTheBiblicalLand = lazy(() => import("./pages/timelines/whoRuledTheBiblicalLand/whoRuledTheBiblicalLand"));
const WomenOfTheBible = lazy(() => import("./pages/womenOfTheBible/womenOfTheBible"));

function App() {
  return (
    <BibleVersionProvider>
      <VerseModalProvider>
        <div className="ctb-appShell">
          {/*
            First focusable element on the page, so keyboard and screen-reader
            users can jump past the header nav straight to the content.
          */}
          <a className="ctb-skipLink" href="#ctb-main">
            Skip to content
          </a>

          <Header />
          <ScrollToTop />
          <RouteMetadata />
          <ModalNavScrollReset />
          <PersonReturnPill />
          <PageReturnPill />
          <VersionUpdatePill />
          <AccountModal />

          {/*
            A real <main>, not a div: axe reported no main landmark on every
            route, and without one it also treats nearly all page content as
            living outside any landmark -- 130 nodes across 8 routes. The skip
            link already targets this element.
          */}
          <main id="ctb-main" className="ctb-appMain" tabIndex={-1}>
          <RouteErrorBoundary>
          <Suspense fallback={<div className="ctb-routeFallback" role="status" aria-live="polite">Loading…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="*" element={<PageNotFound />} />

            <Route path="/adam-noah" element={<AdamNoah />} />
            <Route path="/japheth-lineage" element={<Japheth />} />
            <Route path="/ham-lineage" element={<Ham />} />
            <Route path="/shem-lineage" element={<Shem />} />
            <Route path="/abraham-lineage" element={<Abraham />} />
            <Route path="/haran-lineage" element={<Haran />} />
            <Route path="/nahor-lineage" element={<Nahor />} />
            <Route path="/jacob-lineage" element={<Jacob />} />
            <Route path="/esau-lineage" element={<Esau />} />
            <Route path="/saul-lineage" element={<Saul />} />
            <Route path="/david-lineage" element={<David />} />
            <Route path="/kings-of-judah" element={<Kings />} />
            <Route path="/moses-aaron" element={<MosesAaron />} />
            <Route path="/levi-line" element={<LeviLine />} />
            <Route
              path="/women-of-the-bible"
              element={<WomenOfTheBible />}
            />
            <Route
              path="/enemies-of-the-bible"
              element={<EnemiesOfTheBible />}
            />
            <Route
              path="/early-church-fathers"
              element={<EarlyChurchFathers />}
            />
            <Route
              path="/nations-of-the-bible"
              element={<NationsOfTheBible />}
            />

            {/* Old Testament */}
            <Route path="/genesis" element={<Genesis />} />
            <Route path="/exodus" element={<Exodus />} />
            <Route path="/leviticus" element={<Leviticus />} />
            <Route path="/numbers" element={<Numbers />} />
            <Route
              path="/exodus-deuteronomy"
              element={<JourneyThroughTheWilderness />}
            />

            <Route
              path="/1samuel-2chron"
              element={<DividedKingdoms />}
            />
            <Route path="/joshua" element={<Joshua />} />
            <Route path="/judges" element={<Judges />} />
            <Route
              path="/ezra-nehemiah-esther"
              element={<PostExile />}
            />

            <Route path="/psalms" element={<Psalms />} />
            <Route path="/job" element={<Job />} />
            <Route path="/proverbs" element={<Proverbs />} />
            <Route path="/ecclesiastes" element={<Ecclesiastes />} />
            <Route path="/song-of-songs" element={<SongOfSongs />} />

            <Route
              path="/isaiah"
              element={<MajorProphets defaultBook="isaiah" />}
            />
            <Route
              path="/jeremiah"
              element={<MajorProphets defaultBook="jeremiah" />}
            />
            <Route
              path="/ezekiel"
              element={<MajorProphets defaultBook="ezekiel" />}
            />
            <Route
              path="/daniel"
              element={<MajorProphets defaultBook="daniel" />}
            />
            <Route path="/minor-prophets" element={<MinorProphets />} />

            {/* New Testament */}
            <Route path="/walk-through-the-gospels" element={<WalkThroughTheGospels />}/>
            <Route path="/church-history" element={<ChurchHistory />} />
            <Route path="/church-letters" element={<ChurchLetters />} />
            <Route path="/revelation" element={<Revelation />} />

            <Route
              path="/who-ruled-the-biblical-land"
              element={<WhoRuledTheBiblicalLand />}
            />
            <Route
              path="/david-alexander"
              element={<TableOfHistoricalTimelineDavidTOAlexander />}
            />
            <Route
              path="/between-the-testaments"
              element={<BetweenTheTestaments />}
            />
            <Route
              path="/second-temple-leaders"
              element={<SecondTempleLeadership />}
            />
            <Route
              path="/alexander-earlychurch"
              element={<TableOfHistoricalTimelineAlexanderToEarlyChurch />}
            />
            <Route
              path="/early-church-history"
              element={<EarlyChurchHistory />}
            />

            <Route
              path="/people/:personId"
              element={<PersonProfilePage />}
            />
            <Route path="/battles/:battleId" element={<Battles />} />

            <Route path="/search" element={<SearchPage />} />
            <Route path="/explorers" element={<ExplorersIndex />} />
            <Route path="/my-study" element={<MyStudy />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/events" element={<EventsExplorer />} />
            <Route
              path="/scripture-preservation"
              element={<Codex />}
            />
            <Route
              path="/scripture-formation-recognition"
              element={<CanonFormation />}
            />
          </Routes>
          </Suspense>
          </RouteErrorBoundary>

          </main>

          <Footer />

          {/*
            Renders nothing. It injects Vercel's collector script, which only
            reports once the domain is served by Vercel, so it stays quiet in
            local development. Outside RouteErrorBoundary on purpose: a route
            that fails to load is exactly the pageview worth keeping.
          */}
          <Analytics />
          <SpeedInsights />
        </div>
      </VerseModalProvider>
    </BibleVersionProvider>
  );
}

export default App;